import React, { useEffect, useState } from 'react';
import '../components/AdminSidebar';
import '../styles/AdminMenagment.css';
import { Users, Download, Pencil, Trash2, Mail, Phone, MapPin, Briefcase, Cake, KeyRound, User, X } from 'lucide-react';
import Sidebar from '../components/AdminSidebar';
import { extractErrorMessage } from '../utils/errorUtils';

export default function AdminManagement() {
  // State to hold students data
  const [students, setStudents] = useState([]);
  // Error state
  const [error, setError] = useState(null);
  // Open/close modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Form data state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    group_code: '',
    pesel: '',
    photo: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  // Edit student state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    pesel: '',
    phone_number: '',
    address: '',
    job: '',
    group_code: '',
  });
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  // View student modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [viewPhotoUrl, setViewPhotoUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  // State to hold groups (do selectów)
  const [selectedGroup, setSelectedGroup] = useState([]);
  // Loading state
  const [loading, setLoading] = useState(false);

  // NOWE: filtry przy liście kursantów
  const [filterGroup, setFilterGroup] = useState('');
  // const [filterLecturer, setFilterLecturer] = useState('');

  // Function to open the modal
  const openModal = () => setIsModalOpen(true);
  // Function to close the modal
  const closeModal = () => setIsModalOpen(false);

  // Function to handle form input changes
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };


  // Fetch students data from the backend
  const fetchStudents = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/getAll`
      );
      if (!response.ok) throw new Error('Błąd w pobieraniu danych uczniów');
      const data = await response.json();

      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError(extractErrorMessage(error));
    }
  };

  // Function to handle viewing student details
  const handleView = async (student, e) => {
    // Prevent opening view modal if clicking on action buttons
    if (e && (e.target.closest('.action-buttons') || e.target.closest('.icon-button'))) {
      return;
    }

    try {
      setViewLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/student/${student.student_id}`
      );
      
      if (!response.ok) throw new Error('Błąd pobierania danych ucznia');
      
      const fullStudentData = await response.json();
      setViewStudent(fullStudentData);
      
      // Try to load photo
      try {
        const photoResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/management/student/${student.student_id}/photo`
        );
        if (photoResponse.ok) {
          const blob = await photoResponse.blob();
          const url = URL.createObjectURL(blob);
          setViewPhotoUrl(url);
        } else {
          setViewPhotoUrl(null);
        }
      } catch (photoErr) {
        setViewPhotoUrl(null);
      }
      
      setIsViewModalOpen(true);
    } catch (err) {
      console.error('Błąd pobierania danych:', err);
      alert('Nie udało się pobrać danych ucznia');
    } finally {
      setViewLoading(false);
    }
  };

  // Function to handle student editing - fetch full student data
  const handleEdit = async (student) => {
    try {
      setEditLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/student/${student.student_id}`
      );
      
      if (!response.ok) throw new Error('Błąd pobierania danych ucznia');
      
      const fullStudentData = await response.json();
      setEditStudent(fullStudentData);
      
      // Set form data
      setEditFormData({
        first_name: fullStudentData.first_name || '',
        last_name: fullStudentData.last_name || '',
        email: fullStudentData.email || '',
        date_of_birth: fullStudentData.date_of_birth || '',
        pesel: fullStudentData.pesel || '',
        phone_number: fullStudentData.phone_number || '',
        address: fullStudentData.address || '',
        job: fullStudentData.job || '',
        group_code: fullStudentData.group_code || '',
      });
      
      // Try to load photo preview
      try {
        const photoResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/management/student/${student.student_id}/photo`
        );
        if (photoResponse.ok) {
          const blob = await photoResponse.blob();
          const url = URL.createObjectURL(blob);
          setEditPhotoPreview(url);
        }
      } catch (photoErr) {
        // Photo doesn't exist, that's okay
        setEditPhotoPreview(null);
      }
      
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Błąd pobierania danych:', err);
      alert('Nie udało się pobrać danych ucznia');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditChange = (e) => {
    setEditFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditPhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      // Convert empty date string to null
      const dateOfBirth = editFormData.date_of_birth && editFormData.date_of_birth.trim() !== '' 
        ? editFormData.date_of_birth 
        : null;

      // Create FormData for multipart/form-data request
      const formPayload = new FormData();
      formPayload.append('first_name', editFormData.first_name || '');
      formPayload.append('last_name', editFormData.last_name || '');
      formPayload.append('email', editFormData.email || '');
      formPayload.append('date_of_birth', dateOfBirth || '');
      formPayload.append('pesel', editFormData.pesel || '');
      formPayload.append('phone_number', editFormData.phone_number || '');
      formPayload.append('address', editFormData.address || '');
      formPayload.append('job', editFormData.job || '');
      formPayload.append('group_code', editFormData.group_code || '');
      if (editPhotoFile) formPayload.append('photo', editPhotoFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/student/${editStudent.student_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formPayload
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Błąd edycji ucznia');
      }

      setIsEditModalOpen(false);
      setEditPhotoFile(null);
      setEditPhotoPreview(null);
      fetchStudents();
      alert('Dane ucznia zostały zaktualizowane pomyślnie!');
    } catch (err) {
      console.error('Błąd edycji:', err);
      const errorMessage = extractErrorMessage(err);
      alert(`Błąd: ${errorMessage}`);
    } finally {
      setEditLoading(false);
    }
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formPayload = new FormData();
      formPayload.append('first_name', formData.first_name);
      formPayload.append('last_name', formData.last_name);
      formPayload.append('date_of_birth', formData.date_of_birth);
      formPayload.append('email', formData.email);
      formPayload.append('group_code', formData.group_code);
      formPayload.append('pesel', formData.pesel);
      if (formData.photo) {
        formPayload.append('photo', formData.photo);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/add`,
        {
          method: 'POST',
          body: formPayload,
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Błąd w dodawaniu ucznia');
      } else {
        await fetchStudents();
      }

      // Close the modal and reset form data
      closeModal();
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        email: '',
        group_code: '',
        pesel: '',
        photo: null,
      });
    } catch (error) {
      console.error('Error adding student:', error);
      setError(extractErrorMessage(error));
    }
  };

  // Function to handle student deletion
  const handleDelete = async (student) => {
    if (!student.student_id) {
      console.warn('Brakuje ID studenta!', student);
      return;
    }

    const confirmed = window.confirm(
      `Czy na pewno chcesz usunąć studenta "${student.name}"?`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/management/delete/${student.student_id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        // Refresh the students list after deletion
        await fetchStudents();
      } else {
        let msg = 'Błąd usuwania użytkownika';
        try {
          const data = await response.json();
          if (data.error) msg = data.error;
        } catch (_) {
          /* ignore */
        }
        window.alert(msg);
      }
    } catch (err) {
      console.error('Błąd przy usuwaniu:', err);
    }
  };

  // Fetch students + groups data from the backend
  useEffect(() => {
    async function fetchGroups() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/management/groups`
        );
        const data = await response.json();
        setSelectedGroup(data);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchStudents();
    fetchGroups();
  }, []);

  // ==============================
  // FILTROWANIE
  // ==============================
  const normalizedSearch = searchTerm.toLowerCase();

  // unikalni wykładowcy na podstawie danych studentów
  // const lecturerOptions = Array.from(
  //   new Set(
  //     students
  //       .map(
  //         (student) =>
  //           student.lecturer_name || student.lecturer || student.trainer_name
  //       )
  //       .filter(Boolean)
  //   )
  // );

  const filteredStudents = students
    // tekst (imię, mail, grupa)
    .filter((student) => {
      if (!normalizedSearch) return true;
      return (
        (student.name || '')
          .toLowerCase()
          .includes(normalizedSearch) ||
        (student.email || '')
          .toLowerCase()
          .includes(normalizedSearch) ||
        (student.group || '')
          .toLowerCase()
          .includes(normalizedSearch)
      );
    })
    // filtr po grupie
    .filter((student) =>
      filterGroup ? student.group === filterGroup : true
    )
    // filtr po wykładowcy – backend powinien dodać pole lecturer_name / lecturer
    // .filter((student) => {
    //   if (!filterLecturer) return true;
    //   const lecturerName =
    //     (student.lecturer_name ||
    //       student.lecturer ||
    //       student.trainer_name ||
    //       ''
    //     ).toLowerCase();
    //   return lecturerName === filterLecturer.toLowerCase();
    // })
    // .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (loading) return <p>Ładowanie danych...</p>;

  return (
    <div className='admin-management'>
      {/* Sidebar */}
      <Sidebar />
      {/* Main Content */}
      <div className='main-content'>
        {/* Header */}
        <div className='header'>
          <h1 className='header-title'>Zarządzaj kursantami i ich danymi</h1>
        </div>
        <div className='dashboard'>
          <h2 className='page-title'>Zarządzanie kursantami</h2>
          <p className='page-subtitle'>
            Zarządzaj kursantami i ich danymi
          </p>
          <div className='management-content'>
            <div className='management-card'>
              <div className='card-header-management'>
                {/* LEWA STRONA: tytuł + filtry */}
                <div className='card-header-left'>
                  <h3>Lista kursantów</h3>
                  <div className='management-filters'>
                    {/* FILTR GRUPY */}
                    <select
                      className='management-filter-select'
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                    >
                      <option value=''>Wszystkie grupy</option>
                      {Array.isArray(selectedGroup) &&
                        selectedGroup.map((group) => (
                          <option
                            key={group.group_code}
                            value={group.group_name}
                          >
                            {group.group_name}
                          </option>
                        ))}
                    </select>

                    {/* FILTR WYKŁADOWCY */}
                    {/* <select
                      className='management-filter-select'
                      value={filterLecturer}
                      onChange={(e) =>
                        setFilterLecturer(e.target.value)
                      }
                    >
                      <option value=''>Wszyscy wykładowcy</option>
                      {lecturerOptions.map((lecturerName) => (
                        <option key={lecturerName} value={lecturerName}>
                          {lecturerName}
                        </option>
                      ))}
                    </select> */}
                  </div>
                </div>

                {/* PRAWA STRONA: przyciski */}
                <div className='button-container'>
                  <button className='btn btn-secondary'>
                    <Download
                      size={16}
                      className='icon-style'
                    />
                    Export CSV
                  </button>
                  <button
                    className='btn btn-primary'
                    onClick={openModal}
                  >
                    <Users
                      size={16}
                      className='icon-style'
                    />
                    Dodaj kursanta
                  </button>
                  {isModalOpen && (
                    <div className='modal-overlay-management'>
                      <div className='modal-content-management'>
                        <h2>Dodaj kursanta</h2>
                        <form onSubmit={handleSubmit}>
                          <label className='form-label'>
                            Imię:
                            <input
                              type='text'
                              name='first_name'
                              value={formData.first_name}
                              onChange={handleChange}
                              required
                              placeholder='Imię'
                            />
                          </label>
                          <label className='form-label'>
                            Nazwisko:
                            <input
                              type='text'
                              name='last_name'
                              value={formData.last_name}
                              onChange={handleChange}
                              required
                              placeholder='Nazwisko'
                            />
                          </label>
                          <label className='form-label'>
                            Data urodzenia:
                            <input
                              type='date'
                              name='date_of_birth'
                              value={formData.date_of_birth}
                              onChange={handleChange}
                            />
                          </label>
                          <label className='form-label'>
                            Email:
                            <input
                              type='email'
                              name='email'
                              value={formData.email}
                              onChange={handleChange}
                              required
                              placeholder='Email'
                            />
                          </label>
                          <label className='form-label'>
                            Grupa:
                          </label>
                          <select
                            className='menagment-form-select'
                            name='group_code'
                            value={formData.group_code}
                            onChange={handleChange}
                            required
                          >
                            <option value='' disabled>
                              Wybierz grupę...
                            </option>
                            {Array.isArray(selectedGroup) &&
                              selectedGroup.map((group) => (
                                <option
                                  key={group.group_code}
                                  value={group.group_code}
                                >
                                  {group.group_name}
                                </option>
                              ))}
                          </select>
                          <label className='form-label'>
                            PESEL:
                            <input
                              type='text'
                              name='pesel'
                              value={formData.pesel}
                              onChange={handleChange}
                              placeholder='PESEL'
                            />
                          </label>
                          <label className='form-label'>
                            Zdjęcie:
                            <input
                              type='file'
                              name='photo'
                              accept='image/*'
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  photo: e.target.files[0],
                                })
                              }
                            />
                          </label>
                          <div className='modal-actions'>
                            <button
                              type='submit'
                              className='btn btn-success'
                            >
                              Dodaj
                            </button>
                            <button
                              type='button'
                              className='btn btn-secondary'
                              onClick={closeModal}
                            >
                              Anuluj
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <p className='error-message'>{error}</p>
              )}

              {/* Students Table */}
              {!error && (
                <div className='student-list-container'>
                  <div className='search-container'>
                    <input
                      type='text'
                      className='management-search-input'
                      placeholder='Wyszukaj kursanta po imieniu, nazwisku, e-mailu lub grupie...'
                      value={searchTerm}
                      onChange={(e) =>
                        setSearchTerm(e.target.value)
                      }
                    />
                  </div>
                  <div className='student-grid-row student-list-header'>
                    <span>Imię i Nazwisko</span>
                    <span>Email</span>
                    <span>Grupa</span>
                    <span>Akcje</span>
                  </div>
                  {/* Map through students and display each one */}
                  {filteredStudents.map((student) => (
                    <div
                      key={student.student_id}
                      className='student-grid-row student-row'
                      onClick={(e) => handleView(student, e)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className='student-name'>
                        {student.name}
                      </span>
                      <span className='student-email'>
                        {student.email}
                      </span>
                      <span>{student.group}</span>
                      <div className='action-buttons' onClick={(e) => e.stopPropagation()}>
                        <button
                          className='icon-button edit'
                          onClick={() => handleEdit(student)}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          className='icon-button delete'
                          onClick={() =>
                            handleDelete(student)
                          }
                        >
                          <Trash2 size={18} color='red' />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isEditModalOpen && (
                <div className='modal-overlay-management'>
                  <div className='modal-content-management'>
                    <h2>Edytuj kursanta</h2>
                    {editLoading && !editStudent ? (
                      <p>Ładowanie danych...</p>
                    ) : (
                      <form onSubmit={handleEditSubmit}>
                        <label className='form-label'>
                          Imię:
                          <input
                            type='text'
                            name='first_name'
                            value={editFormData.first_name}
                            onChange={handleEditChange}
                            required
                            placeholder='Imię'
                          />
                        </label>
                        <label className='form-label'>
                          Nazwisko:
                          <input
                            type='text'
                            name='last_name'
                            value={editFormData.last_name}
                            onChange={handleEditChange}
                            required
                            placeholder='Nazwisko'
                          />
                        </label>
                        <label className='form-label'>
                          Email:
                          <input
                            type='email'
                            name='email'
                            value={editFormData.email}
                            onChange={handleEditChange}
                            required
                            placeholder='Email'
                          />
                        </label>
                        <label className='form-label'>
                          Data urodzenia:
                          <input
                            type='date'
                            name='date_of_birth'
                            value={editFormData.date_of_birth}
                            onChange={handleEditChange}
                          />
                        </label>
                        <label className='form-label'>
                          PESEL:
                          <input
                            type='text'
                            name='pesel'
                            value={editFormData.pesel}
                            onChange={handleEditChange}
                            placeholder='PESEL'
                          />
                        </label>
                        <label className='form-label'>
                          Numer telefonu:
                          <input
                            type='tel'
                            name='phone_number'
                            value={editFormData.phone_number}
                            onChange={handleEditChange}
                            placeholder='Numer telefonu'
                          />
                        </label>
                        <label className='form-label'>
                          Adres:
                          <input
                            type='text'
                            name='address'
                            value={editFormData.address}
                            onChange={handleEditChange}
                            placeholder='Adres'
                          />
                        </label>
                        <label className='form-label'>
                          Zawód:
                          <input
                            type='text'
                            name='job'
                            value={editFormData.job}
                            onChange={handleEditChange}
                            placeholder='Zawód'
                          />
                        </label>
                        <label className='form-label'>
                          Grupa:
                        </label>
                        <select
                          className='menagment-form-select'
                          name='group_code'
                          value={editFormData.group_code}
                          onChange={handleEditChange}
                        >
                          <option value=''>
                            Wybierz grupę...
                          </option>
                          {Array.isArray(selectedGroup) &&
                            selectedGroup.map((group) => (
                              <option
                                key={group.group_code}
                                value={group.group_code}
                              >
                                {group.group_name}
                              </option>
                            ))}
                        </select>
                        <label className='form-label'>
                          Zdjęcie profilowe:
                          <input
                            type='file'
                            name='photo'
                            accept='image/*'
                            onChange={handleEditPhotoChange}
                          />
                          {editPhotoPreview && (
                            <div style={{ marginTop: '10px' }}>
                              <img
                                src={editPhotoPreview}
                                alt='Preview'
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                  border: '2px solid #e2e8f0'
                                }}
                              />
                            </div>
                          )}
                        </label>
                        <div className='modal-actions'>
                          <button
                            className='btn btn-success'
                            type='submit'
                            disabled={editLoading}
                          >
                            {editLoading ? 'Zapisywanie...' : 'Zapisz zmiany'}
                          </button>
                          <button
                            className='btn btn-secondary'
                            type='button'
                            onClick={() => {
                              setIsEditModalOpen(false);
                              setEditPhotoFile(null);
                              setEditPhotoPreview(null);
                            }}
                            disabled={editLoading}
                          >
                            Anuluj
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}

              {/* View Student Modal */}
              {isViewModalOpen && (
                <div className='modal-overlay-management'>
                  <div className='modal-content-management modal-view-student'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2>Szczegóły kursanta</h2>
                      <button
                        className='icon-button'
                        onClick={() => {
                          setIsViewModalOpen(false);
                          if (viewPhotoUrl) {
                            URL.revokeObjectURL(viewPhotoUrl);
                          }
                          setViewPhotoUrl(null);
                          setViewStudent(null);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                      >
                        <X size={24} />
                      </button>
                    </div>
                    {viewLoading ? (
                      <p>Ładowanie danych...</p>
                    ) : viewStudent ? (
                      <div className='student-view-content'>
                        {/* Photo */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                          {viewPhotoUrl ? (
                            <img
                              src={viewPhotoUrl}
                              alt='Student'
                              style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                border: '3px solid #e2e8f0'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '120px',
                              height: '120px',
                              borderRadius: '50%',
                              backgroundColor: '#f1f5f9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '36px',
                              fontWeight: '600',
                              color: '#64748b',
                              border: '3px solid #e2e8f0'
                            }}>
                              {viewStudent.first_name?.charAt(0) || ''}
                              {viewStudent.last_name?.charAt(0) || ''}
                            </div>
                          )}
                        </div>

                        {/* Student Information */}
                        <div className='student-info-grid'>
                          <div className='student-info-item'>
                            <User size={18} color='#64748b' />
                            <div>
                              <div className='info-label'>Imię i Nazwisko</div>
                              <div className='info-value'>
                                {viewStudent.first_name} {viewStudent.last_name}
                              </div>
                            </div>
                          </div>

                          <div className='student-info-item'>
                            <Mail size={18} color='#64748b' />
                            <div>
                              <div className='info-label'>Email</div>
                              <div className='info-value'>{viewStudent.email || 'Brak'}</div>
                            </div>
                          </div>

                          {viewStudent.student_number && (
                            <div className='student-info-item'>
                              <User size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Numer indeksu</div>
                                <div className='info-value'>{viewStudent.student_number}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.group_code && (
                            <div className='student-info-item'>
                              <Users size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Grupa</div>
                                <div className='info-value'>{viewStudent.group_code}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.date_of_birth && (
                            <div className='student-info-item'>
                              <Cake size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Data urodzenia</div>
                                <div className='info-value'>
                                  {new Date(viewStudent.date_of_birth).toLocaleDateString('pl-PL')}
                                </div>
                              </div>
                            </div>
                          )}

                          {viewStudent.pesel && (
                            <div className='student-info-item'>
                              <KeyRound size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>PESEL</div>
                                <div className='info-value'>{viewStudent.pesel}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.phone_number && (
                            <div className='student-info-item'>
                              <Phone size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Numer telefonu</div>
                                <div className='info-value'>{viewStudent.phone_number}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.address && (
                            <div className='student-info-item'>
                              <MapPin size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Adres</div>
                                <div className='info-value'>{viewStudent.address}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.job && (
                            <div className='student-info-item'>
                              <Briefcase size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Zawód</div>
                                <div className='info-value'>{viewStudent.job}</div>
                              </div>
                            </div>
                          )}

                          {viewStudent.username && (
                            <div className='student-info-item'>
                              <User size={18} color='#64748b' />
                              <div>
                                <div className='info-label'>Nazwa użytkownika</div>
                                <div className='info-value'>{viewStudent.username}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className='modal-actions' style={{ marginTop: '24px' }}>
                          <button
                            className='btn btn-primary'
                            onClick={() => {
                              setIsViewModalOpen(false);
                              if (viewPhotoUrl) {
                                URL.revokeObjectURL(viewPhotoUrl);
                              }
                              setViewPhotoUrl(null);
                              handleEdit(viewStudent);
                            }}
                          >
                            <Pencil size={16} style={{ marginRight: '8px' }} />
                            Edytuj
                          </button>
                          <button
                            className='btn btn-secondary'
                            onClick={() => {
                              setIsViewModalOpen(false);
                              if (viewPhotoUrl) {
                                URL.revokeObjectURL(viewPhotoUrl);
                              }
                              setViewPhotoUrl(null);
                              setViewStudent(null);
                            }}
                          >
                            Zamknij
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p>Brak danych do wyświetlenia</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
